import { Control, FieldErrors, UseFormGetValues, UseFormRegister, UseFormResetField, UseFormSetValue, UseFormTrigger, UseFormWatch } from "react-hook-form"
import { UserDataType } from "types/autentication"
import { INotificationPlan, NotificationType } from "types/notification"


export type FocusedInputType = {
  inputname?: string
  limit?: number
}
export type ToDeleteElementType = {
  index: number
  actionId: string
}
export type StepFourType = {
  sys_id: string
  id: string
  how: string
  how_much: string
  what: string
  when: any
  where: string
  who: string
  why: string
}
export const actionPlanTableHeader = [
  {
    title: 'O QUE',
    subtitle: '(SERÁ FEITO) WHAT'
  },
  {
    title: 'POR QUE',
    subtitle: '(SERÁ FEITO) WHY'
  },
  {
    title: 'ONDE',
    subtitle: '(SERÁ FEITO) WHERE'
  },
  {
    title: 'QUANDO',
    subtitle: '(SERÁ FEITO) WHEN'
  },
  {
    title: 'POR QUEM',
    subtitle: '(SERÁ FEITO) WHO'
  },
  {
    title: 'COMO',
    subtitle: '(SERÁ FEITO) HOW'
  },
  {
    title: 'QUANTO',
    subtitle: '(CUSTARÁ) HOW MUCH'
  }
]
export type ErrorStep4Type = FieldErrors<{
  fields: {
    sys_id?: string
    id: string
    how: string
    how_much: string
    what: string
    when: string
    where: string
    who: string
    why: string
  }[]
}>
export interface TreatmentsStep4Props {
  id: string
  notificationById: NotificationType
  notificationStatusState: string
  tooltipAlert: string
  statusIsArchivedOrFinished: boolean
  currentUser: UserDataType

}
export interface IActionsComponent {
  id: string
  trigger: UseFormTrigger<any>
  actionPlans: INotificationPlan[]
  notificationById: NotificationType
  notificationStatusState: string
  tooltipAlert: string
  register: UseFormRegister<{ fields: StepFourType[] }>
  setValue: UseFormSetValue<{ fields: StepFourType[] }>
  resetField: UseFormResetField<{ fields: StepFourType[] }>
  control: Control<{ fields: StepFourType[] }, any>
  isDirty: boolean
  statusIsArchivedOrFinished: boolean
  reset: any
  getValues: UseFormGetValues<{ fields: StepFourType[] }>
  watch: UseFormWatch<{ fields: StepFourType[] }>
  errors: ErrorStep4Type
  currentUser: UserDataType
  dirtyFieldsStep4: Partial<
    Readonly<{
      fields?: {
        sys_id?: boolean
        id?: boolean
        how?: boolean
        how_much?: boolean
        what?: boolean
        when?: boolean
        where?: boolean
        who?: boolean
        why?: boolean
      }[]
    }>
  >
}
export interface ActionPlanTableBodyProps {
  trigger: UseFormTrigger<any>
  disabledForRole: boolean
  index: number
  isStaticData?:boolean
  register?: UseFormRegister<{ fields: StepFourType[] }>
  control?: Control<{ fields: StepFourType[] }, any>
  handleDelete: (idx: number) => void
  setFocusedInputData?: React.Dispatch<
    React.SetStateAction<{
      inputname?: string
      limit?: number
    }>
  >
  errors: ErrorStep4Type
  setValue?: UseFormSetValue<{ fields: StepFourType[] }>
  getValues?: UseFormGetValues<{ fields: StepFourType[] }>
}
export const listOfQuestions = [
  'how',
  'what',
  'when',
  'where',
  'who',
  'why',
  'how_much'
]
export type QuestionsTypes = 'how' | 'what' | 'when' | 'where' | 'who' | 'why' | 'how_much';

/**
 * Converte dados do plano de ação individual para formato batch CREATE
 * @param notificationId - ID da notificação individual
 * @param currentUser - Usuário atual
 * @param doNotPerform - Se deve marcar como "não realizar"
 * @returns IBatchActionPlanRequest - Request formatado para batch CREATE
 */
export function convertNotPerformToBatchRequest(
  notificationId: string,
  currentUser: UserDataType,
  doNotPerform: boolean = true
): any {
  if (!notificationId || !currentUser) {
    throw new Error('ID da notificação ou usuário não encontrados');
  }

  const records = [{
    hospital_incident: notificationId,
    how: " ",
    how_much: " ",
    what: " ",
    when: " ",
    where: " ",
    who: " ",
    why: " ",
    u_notifica_user: currentUser?.sys_id || '',
    u_do_not_perform_optional: doNotPerform
  }];

  return {
    records,
    options: {
      stopOnError: false
    }
  };
}

/**
 * Converte dados do plano de ação individual para formato batch UPDATE
 * @param existingPlans - Planos existentes que serão atualizados
 * @param currentUser - Usuário atual
 * @param doNotPerform - Se deve marcar como "não realizar"
 * @returns IBatchActionPlanRequest - Request formatado para batch UPDATE
 */
export function convertNotPerformToUpdateBatchRequest(
  existingPlans: any[],
  currentUser: UserDataType,
  doNotPerform: boolean = true
): any {
  if (!existingPlans || existingPlans.length === 0) {
    throw new Error('Nenhum plano existente encontrado para atualizar');
  }

  // Para UPDATE, manter todos os campos existentes e só alterar u_do_not_perform_optional
  const records = existingPlans.map(actionPlan => ({
    sys_id: actionPlan.sys_id, // Obrigatório para UPDATE
    u_do_not_perform_optional: doNotPerform,
    // Manter todos os outros campos existentes
    how: actionPlan.how || '',
    how_much: actionPlan.how_much || '',
    what: actionPlan.what || '',
    when: actionPlan.when || '',
    where: actionPlan.where || '',
    who: actionPlan.who || '',
    why: actionPlan.why || '',
    u_notifica_user: currentUser?.sys_id || actionPlan.u_notifica_user || ''
  }));

  return {
    records,
    options: {
      stopOnError: false
    }
  };
}

/**
 * Converte plano de ação individual para formato batch
 * @param actionPlan - Plano de ação individual
 * @param notificationId - ID da notificação
 * @param currentUser - Usuário atual
 * @returns IBatchActionPlanRequest - Request formatado para batch
 */
export function convertActionPlanToBatchRequest(
  actionPlan: StepFourType,
  notificationId: string,
  currentUser: UserDataType
): any {
  if (!notificationId || !currentUser) {
    throw new Error('ID da notificação ou usuário não encontrados');
  }

  const records = [{
    hospital_incident: notificationId,
    how: actionPlan.how || " ",
    how_much: actionPlan.how_much || " ",
    what: actionPlan.what || " ",
    when: actionPlan.when || " ",
    where: actionPlan.where || " ",
    who: actionPlan.who || " ",
    why: actionPlan.why || " ",
    u_notifica_user: currentUser?.sys_id || " ",
    u_do_not_perform_optional: 'false'
  }];

  return {
    records,
    options: {
      stopOnError: false
    }
  };
}

/**
 * Converte ID do plano de ação individual para formato batch DELETE
 * @param actionPlanId - ID do plano de ação para deletar
 * @returns IBatchActionPlanRequest - Request formatado para batch DELETE
 */
export function convertDeleteActionPlanToBatchRequest(
  actionPlanId: string
): any {
  if (!actionPlanId || actionPlanId.trim() === '') {
    throw new Error('ID do plano de ação não encontrado');
  }

  // Para DELETE, apenas uma lista de sys_ids como strings
  const records = [actionPlanId];

  return {
    records,
    options: {
      stopOnError: false
    }
  };
}